/**
 * Contexte plan — condensé des entités par observation (ou par groupe de
 * détection), envoyé à l'IA et affiché dans les panneaux.
 *
 * Code déplacé tel quel depuis `composable/useObservationPlanLinks.ts` (qui le
 * ré-exporte) : la fonction est pure et sert aussi au module d'analyse du plan
 * (`grouping.ts`), qui doit rester testable sans le moteur ODA.
 */

import type { EntityOrientation } from "@/drawing/meta/measure";

/**
 * Champs qu'une entité doit exposer pour être condensée dans le contexte plan.
 * `ObservationEntityRef` (scan XData) et `AnalysisEntity` (analyse du plan)
 * les portent tous deux.
 */
export interface PlanMeasurableEntity {
  /** Nature CAD de l'entité (EntityType du DR_META). */
  kind: string;
  /** Nom du note_config (désordre de la légende) actif au dessin, si connu. */
  defectName: string | null;
  /** Calque de l'entité. */
  layer: string;
  /** Longueur développée (unités du plan), null si non mesurable. */
  length: number | null;
  /** Surface (contour fermé, unités²), null sinon. */
  area: number | null;
  /** Orientation dominante (tracé linéaire ouvert uniquement), null sinon. */
  orientation: EntityOrientation | null;
  /** Contenu textuel (texte, MText, MLeader) — les mots de l'inspecteur. */
  text: string | null;
  /** Numéro du marqueur photo (entités marqueur uniquement). */
  markerNumber: number | null;
}

/** Groupe d'entités similaires d'une observation, envoyé à l'IA. */
export interface PlanEntityGroup {
  kind: string;
  defectName: string | null;
  count: number;
  /** Libellé de la note dans la légende (si différent du nom). */
  noteLabel?: string;
  /** Catégorie (catalogue) de la note dans la légende. */
  category?: string;
  /** Calque des entités. */
  layer?: string;
  /**
   * Somme des longueurs des tracés LINÉAIRES (unités du plan), arrondie.
   * Les contours fermés n'y contribuent jamais : leur périmètre n'est pas une
   * longueur de désordre (« végétation sur 40 m » — retour terrain).
   */
  totalLength?: number;
  /** Somme des surfaces des contours fermés (unités² du plan), arrondie. */
  totalArea?: number;
  /** Longueurs individuelles des tracés linéaires. */
  lengths?: number[];
  /** Orientation commune des tracés linéaires (si uniforme dans le groupe). */
  orientation?: EntityOrientation;
  /** Orientations individuelles (si elles diffèrent au sein du groupe). */
  orientations?: EntityOrientation[];
}

/** Résumé des entités d'une observation, envoyé à l'IA de constatations. */
export interface ObservationPlanContext {
  entities: PlanEntityGroup[];
  markerNumbers: number[];
  /** Textes écrits SUR le plan et rattachés à l'observation (mots de l'inspecteur). */
  texts?: string[];
}

/** Map `localId` d'observation → contexte plan (payload de generate-findings). */
export type ObservationPlanContextMap = Record<string, ObservationPlanContext>;

/** Libellés + catégories des notes de la légende active, résolus par nom. */
export interface LegendNoteInfoMap {
  /** name → { label?, category? } résolu de la légende active. */
  noteInfo: Map<string, { label?: string; category?: string }>;
}

const round2 = (value: number): number => Math.round(value * 100) / 100;

/**
 * Condense le scan XData en contexte pour l'IA : par observation, les entités
 * groupées par nature + désordre de légende (avec libellé/catégorie résolus
 * depuis la légende active, calque et mesures), les numéros de marqueurs et
 * les textes posés sur le plan.
 */
export function buildPlanContext(
  entitiesByObservation: Map<string, PlanMeasurableEntity[]>,
  legend?: LegendNoteInfoMap
): ObservationPlanContextMap {
  const out: ObservationPlanContextMap = {};
  for (const [localId, refs] of entitiesByObservation) {
    const groups = new Map<string, PlanEntityGroup>();
    const markerNumbers = new Set<number>();
    const texts: string[] = [];

    for (const entityRef of refs) {
      if (entityRef.markerNumber !== null) {
        markerNumbers.add(entityRef.markerNumber);
      }
      const text = entityRef.text?.trim();
      if (text) texts.push(text);

      const key = `${entityRef.kind}|${entityRef.defectName ?? ""}`;
      let group = groups.get(key);
      if (!group) {
        const info = entityRef.defectName
          ? legend?.noteInfo.get(entityRef.defectName)
          : undefined;
        group = {
          kind: entityRef.kind,
          defectName: entityRef.defectName,
          count: 0,
          ...(info?.label && info.label !== entityRef.defectName
            ? { noteLabel: info.label }
            : {}),
          ...(info?.category ? { category: info.category } : {}),
          layer: entityRef.layer,
        };
        groups.set(key, group);
      }
      group.count++;

      if (entityRef.area !== null) {
        // Contour fermé : seule la SURFACE a un sens métier. Le `length`
        // renvoyé par la mesure est le périmètre — ne jamais l'envoyer comme
        // longueur à l'IA (elle en ferait « végétation sur 40 m »).
        group.totalArea = round2((group.totalArea ?? 0) + entityRef.area);
      } else if (entityRef.length !== null) {
        group.totalLength = round2((group.totalLength ?? 0) + entityRef.length);
        group.lengths = [...(group.lengths ?? []), round2(entityRef.length)];
        if (entityRef.orientation) {
          group.orientations = [
            ...(group.orientations ?? []),
            entityRef.orientation,
          ];
        }
      }
    }

    // Orientation : un seul champ si elle est uniforme dans le groupe.
    for (const group of groups.values()) {
      const orientations = group.orientations;
      if (orientations?.length) {
        const first = orientations[0];
        if (orientations.every((o) => o === first)) {
          group.orientation = first;
          delete group.orientations;
        }
      }
    }

    // Une forme fermée remplie = polyligne + hachure appariées, toutes deux
    // estampées avec le même defectName : la hachure est un remplissage, pas
    // un désordre de plus — son `count` fausserait le dénombrement de l'IA.
    const allGroups = [...groups.values()];
    const entities = allGroups.filter(
      (group) =>
        group.kind !== "hatch" ||
        !allGroups.some(
          (other) =>
            other !== group &&
            other.kind !== "hatch" &&
            other.defectName === group.defectName,
        ),
    );

    out[localId] = {
      entities,
      markerNumbers: [...markerNumbers].sort((a, b) => a - b),
      ...(texts.length ? { texts } : {}),
    };
  }
  return out;
}
